
import { AddLocation, Delete, Edit } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MapEntityPoint, MapEntityRef } from '../canvas/types';
import { WorldEntityPicker } from './WorldEntityPicker';
import { useNovel } from '@/context/novel-context';

interface WorldPointsListProps {
    points: MapEntityPoint[];
    isPlacingPoint: boolean;
    onStartPlacing: () => void;
    onStopPlacing: () => void;
    onDelete: (uid: string) => void;
    onChangeLabel: (uid: string, label: string) => void;
    onChangeEntity: (uid: string, ref: MapEntityRef) => void;
    selectedUid: string | null;
    onSelectUid: (uid: string | null) => void;
    currentLocationId?: string;
}

export function WorldPointsList({
    points, isPlacingPoint, onStartPlacing, onStopPlacing,
    onDelete, onChangeLabel, onChangeEntity,
    selectedUid, onSelectUid, currentLocationId,
}: WorldPointsListProps) {
    const { t } = useTranslation();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [editingUid, setEditingUid] = useState<string | null>(null);
    const [labelDraft, setLabelDraft] = useState('');
    const [relinkinUid, setRelinkingUid] = useState<string | null>(null);

    const { novel } = useNovel();
    const locationSummaries = novel?.summaries?.locations || {};
    const eventSummaries = novel?.summaries?.events || {};

    const getEntityName = (ref: { type: 'locations' | 'events', id: string, name: string }) => {
        if (ref.type === 'locations') {
            const sum = locationSummaries[ref.id];
            if (sum) return sum.name;
        } else if (ref.type === 'events') {
            const sum = eventSummaries[ref.id];
            if (sum) return sum.name;
        }
        return ref.name;
    };

    const handleStartLabelEdit = (pt: MapEntityPoint) => {
        setEditingUid(pt.uid);
        setLabelDraft(pt.label ?? getEntityName(pt.entityRef));
    };

    const handleSaveLabel = () => {
        if (editingUid) {
            onChangeLabel(editingUid, labelDraft.trim() || '');
        }
        setEditingUid(null);
    };

    const handleRelink = (uid: string) => {
        setRelinkingUid(uid);
        setPickerOpen(true);
    };

    const handlePickerSelect = (ref: MapEntityRef) => {
        if (relinkinUid) {
            onChangeEntity(relinkinUid, ref);
            setRelinkingUid(null);
        }
        setPickerOpen(false);
    };

    const entityTypeColor = (type: string) =>
        type === 'locations' ? 'primary' : 'secondary';

    return (
        <Stack spacing={1.5}>
            <Button
                variant={isPlacingPoint ? 'contained' : 'outlined'}
                color={isPlacingPoint ? 'warning' : 'primary'}
                startIcon={<AddLocation />}
                fullWidth
                size="small"
                onClick={isPlacingPoint ? onStopPlacing : onStartPlacing}
            >
                {isPlacingPoint 
                    ? t('map_creator.world_points.placing') 
                    : t('map_creator.world_points.place_button')}
            </Button>

            {isPlacingPoint && (
                <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                    <Typography variant="caption" fontWeight="bold">
                        {t('map_creator.world_points.place_instructions')}
                    </Typography>
                </Box>
            )}

            <Divider />

            <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase', color: 'text.secondary' }}>
                {t('map_creator.world_points.placed_title', { count: points.length })}
            </Typography>

            {points.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                    {t('map_creator.world_points.empty_points')}
                </Typography>
            )}

            <List dense disablePadding>
                {points.map(pt => (
                    <ListItem
                        key={pt.uid}
                        disablePadding
                        sx={{
                            mb: 0.5, borderRadius: 1, border: '1px solid',
                            borderColor: selectedUid === pt.uid ? 'primary.main' : 'divider',
                            bgcolor: selectedUid === pt.uid ? 'action.selected' : 'background.paper',
                            cursor: 'pointer', flexDirection: 'column', alignItems: 'stretch', p: 1,
                        }}
                        onClick={() => onSelectUid(selectedUid === pt.uid ? null : pt.uid)}
                    >
                        {editingUid === pt.uid ? (
                            <Stack direction="row" spacing={0.5}>
                                <TextField
                                    size="small"
                                    value={labelDraft}
                                    onChange={e => setLabelDraft(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') handleSaveLabel(); if (e.key === 'Escape') setEditingUid(null); }}
                                    autoFocus
                                    fullWidth
                                    sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                                />
                                <Button size="small" onClick={handleSaveLabel} variant="contained">✓</Button>
                                <Button size="small" onClick={() => setEditingUid(null)}>✕</Button>
                            </Stack>
                        ) : (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <ListItemText
                                    primary={pt.label || getEntityName(pt.entityRef)}
                                    secondary={getEntityName(pt.entityRef) !== (pt.label || getEntityName(pt.entityRef)) ? getEntityName(pt.entityRef) : undefined}
                                    primaryTypographyProps={{ variant: 'body2', fontSize: '0.75rem', fontWeight: 'bold' }}
                                    secondaryTypographyProps={{ variant: 'caption', fontSize: '0.65rem' }}
                                    sx={{ flex: 1, m: 0 }}
                                />
                                <Chip
                                    label={pt.entityRef.type === 'locations' 
                                        ? t('map_creator.world_points.chips.location') 
                                        : t('map_creator.world_points.chips.event')}
                                    size="small"
                                    color={entityTypeColor(pt.entityRef.type)}
                                    sx={{ height: 16, fontSize: '0.6rem' }}
                                />
                                <Tooltip title={t('map_creator.world_points.tooltips.rename')}>
                                    <IconButton size="small" onClick={e => { e.stopPropagation(); handleStartLabelEdit(pt); }}>
                                        <Edit sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t('map_creator.world_points.tooltips.relink')}>
                                    <IconButton size="small" onClick={e => { e.stopPropagation(); handleRelink(pt.uid); }}>
                                        <AddLocation sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t('map_creator.world_points.tooltips.delete')}>
                                    <IconButton size="small" color="error" onClick={e => { e.stopPropagation(); onDelete(pt.uid); }}>
                                        <Delete sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        )}
                    </ListItem>
                ))}
            </List>

            <WorldEntityPicker
                open={pickerOpen}
                onClose={() => { setPickerOpen(false); setRelinkingUid(null); }}
                onSelect={handlePickerSelect}
                currentLocationId={currentLocationId}
            />
        </Stack>
    );
}
